/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  const rootPath = () => window.Shopify?.routes?.root || '/';

  class ProductDetails extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.variantSelector = this.querySelector('[data-product-variant-selector]');
      this.variantSelector?.addEventListener('change', () => this.updateVariant(), { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    updateVariant() {
      const option = this.variantSelector?.selectedOptions?.[0];
      if (!option) return;

      const available = option.dataset.available === 'true';
      const comparePrice = option.dataset.comparePrice || '';
      const price = this.querySelector('[data-product-price]');
      const compare = price?.querySelector('[data-compare-price]');

      const current = price?.querySelector('[data-current-price]');
      if (current) current.textContent = option.dataset.price || '';
      if (compare) {
        compare.textContent = comparePrice;
        compare.hidden = !comparePrice;
      }
      price?.classList.toggle('product-price--sale', Boolean(comparePrice));

      this.querySelectorAll('[data-product-sku]').forEach((sku) => {
        sku.textContent = option.dataset.sku || '';
      });
      this.querySelectorAll('[data-product-variant-input]').forEach((input) => {
        input.value = option.value;
      });
      this.querySelectorAll('[data-product-submit]').forEach((button) => {
        button.disabled = !available;
        const label = button.querySelector('[data-product-submit-label]');
        if (label) label.textContent = available ? button.dataset.addLabel : option.dataset.priceReady === 'false' ? 'Currently unavailable' : button.dataset.soldOutLabel;
      });

      const mediaId = option.dataset.mediaId;
      if (mediaId) this.querySelector('product-gallery')?.selectMedia(mediaId);
      if (option.dataset.url) window.history.replaceState({}, '', option.dataset.url);

      this.dispatchEvent(new CustomEvent('theme:variant-changed', {
        bubbles: true,
        detail: { id: Number(option.value), available, mediaId }
      }));
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-product-submit]');
      this.error = this.querySelector('[data-product-form-error]');
      this.form?.addEventListener('submit', (event) => this.addToCart(event), { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.clearTimeout(this.successTimer);
    }

    async addToCart(event) {
      event.preventDefault();
      if (!this.form || !this.button || this.button.disabled || this.loading) return;

      this.loading = true;
      this.setError('');
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${rootPath()}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.errorMessage);

        const cartResponse = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        document.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart } }));
        const productTitle = this.dataset.productTitle || result.product_title || result.title || '';
        const productImage = this.dataset.productImage || result.featured_image?.url || result.image || '';
        document.dispatchEvent(new CustomEvent('theme:toast', {
          detail: {
            message: productTitle ? '' : this.dataset.successMessage,
            type: 'success',
            label: this.dataset.productAddedLabel || 'Product added',
            product: productTitle ? { title: productTitle, image: productImage, alt: productTitle } : null
          }
        }));

        this.button.classList.remove('is-loading');
        this.button.classList.add('is-success');
        window.clearTimeout(this.successTimer);
        this.successTimer = window.setTimeout(() => this.button?.classList.remove('is-success'), 900);
      } catch (error) {
        const message = error instanceof Error ? error.message : this.dataset.errorMessage;
        this.setError(message);
        document.dispatchEvent(new CustomEvent('theme:toast', {
          detail: { message, type: 'error' }
        }));
      } finally {
        this.loading = false;
        this.button?.classList.remove('is-loading');
        this.button?.removeAttribute('aria-busy');
      }
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }

  if (!customElements.get('product-details')) customElements.define('product-details', ProductDetails);
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
})();
